import React from 'react';
import { Info } from 'lucide-react';

const DhlSummaryTable = ({ dhlEstimation, cif, totalItemsValue }) => {
    // console.log("CIFCIFCIF", cif);

    let CIF = cif;

    if (!dhlEstimation) return null;

    // Countries that need special tax calculations
    const specialVATCountries = ['CM', 'HN', 'MD', 'NA', 'SZ', 'EC', 'SC', 'LR', 'IL', 'LB', 'MW', 'GR'];

    // Tax formulas and display text configurations
    const taxConfigs = {
        CM: { rate: 0.175, type: 'VAT', formula: '(17.5%) based on Duty + CIF value' }, //  Cameroon - CM
        HN: { rate: 0.150, type: 'SALE', formula: '(15%) based on Duty + CIF value' }, // Honduras - HN  
        SZ: { rate: 0.150, type: 'VAT', formula: '(15%) based on Total Items Value + Duty' }, //  Eswatini (Swaziland) - SZ
        EC: { rate: 0.150, type: 'VAT', formula: '(15%) based on Total Items Value + Freight + Insurance + DUTY + FODINFA + ICE + SAFEGUARD' }, // Ecuador - EC
        MD: { rate: 0.200, type: 'VAT', formula: '(20%) based on Duty + Items Value + Freight + Insurance' }, // Moldova - MD
        SC: { rate: 0.150, type: 'VAT', formula: '(15%) based on Duty + Items Value + Freight + Insurance' }, // Seychelles - SC
        LR: { rate: 0.100, type: 'GST', formula: '(10%) based on Items Value + Freight + Insurance + DUTY + ECOWAS_TRADE_LEVY' }, // Liberia - LR
        NA: { rate: 0.15, type: 'VAT', formula: '(110% of 15%) based on Items Value' }, // Namibia - NA
        IL: { rate: 0.180, type: 'VAT', formula: '(18%) based on Items Value + Freight + Insurance  + DUTY' }, // Israel - IL
        LB: { rate: 0.110, type: 'VAT', formula: '(11%) based on Duty + CIF + ADDITIONAL_DUTY' }, // Lebanon - LB
        MW: { rate: 0.165, type: 'VAT', formula: '(16.5%) based on Duty + CIF value' }, // Malawi - MW
        GR: { rate: 0.240, type: 'VAT', formula: '(24%) based on Total Items Value + Freight + Insurance + DUTY' }, // Added Greece
    };

    // Function to calculate Extra Tax for Turkey
    const calculateTRExtraTax = (CIF, hsCode) => {
        if (hsCode && hsCode.startsWith('8')) {
            return CIF * 0.05; // 5% of CIF
        }
        return 0;
    };

    // Function to calculate LAW fee for Costa Rica
    const calculateCRLaw = (CIF) => {
        return CIF * 0.01; // 1% of CIF
    };

    // Function to calculate Extra Tax for Ivory Coast
    const calculateCIExtraTax = (CIF) => {
        const extraTax = CIF * 0.05; // 5% of CIF
        return Math.max(extraTax, 300); // Return the greater of 5% or 300
    };

    // Function to calculate special taxes
    const calculateSpecialTax = (dutyAmount, CIF, countryCode, totalItemsValue) => {
        const config = taxConfigs[countryCode];
        if (!config) return 0;

        if (countryCode === 'GR') {
            return config.rate * (CIF + dutyAmount);
        }

        if (countryCode === 'LB') {
            const addyFee = dhlEstimation.feeTotals.find(fee => fee.name === 'ADDY')?.value || 0;
            return config.rate * (dutyAmount + CIF + addyFee);
        }

        if (countryCode === 'NA') {
            // Namibia: VAT = 110% * (15% of Items Value)
            return (config.rate * totalItemsValue) * 1.1;
        }

        if (countryCode === 'EC') {
            // Find all relevant fees for Ecuador VAT calculation
            const relevantFees = ['DUTY', 'FODINFA', 'ICE', 'SAFEGUARD'];
            const feesTotal = dhlEstimation.feeTotals
                .filter(fee => relevantFees.includes(fee.name))
                .reduce((sum, fee) => sum + fee.value, 0);

            return config.rate * (totalItemsValue + feesTotal);
        }

        if (countryCode === 'SZ') {
            return config.rate * (totalItemsValue + dutyAmount);
        }

        if (countryCode === 'LR') {
            // Calculate VAT(GST) for Liberia
            const ecowasLevy = dhlEstimation.feeTotals.find(fee => fee.name === 'ECOWAS_TRADE_LEVY')?.value || 0;
            return config.rate * (CIF + dutyAmount + ecowasLevy);
        }

        if (countryCode === 'MW') {
            // Malawi VAT calculation: 16.5% of (Duty + CIF)
            return config.rate * (dutyAmount + CIF);
        }

        return config.rate * (dutyAmount + CIF);
    };

    // Function to calculate IMPROC for Australia
    const calculateAUImproc = (CIF) => {
        return CIF * 0.012; // 1.2% of CIF
    };

    // Function to calculate ICF for Comoros
    const calculateKMIcf = (CIF) => {
        return CIF * 0.025; // 2.5% of CIF
    };

    // Function to get display name for tax types
    const getTaxDisplayName = (originalName, countryCode) => {
        if (['SALE', 'GST'].includes(originalName)) {
            return `VAT (${originalName})`;
        }
        return originalName;
    };

    // Function to get formula tooltip text
    const getFormulaTooltip = (taxName, countryCode) => {
        if (countryCode === 'AU' && taxName === 'IMPROC') {
            return '(1.2%) based on CIF';
        }
        if (countryCode === 'CR' && taxName === 'LAW') {
            return '(1%) based on CIF';
        }
        if (countryCode === 'CI' && taxName === 'EXTRA_TAX') {
            return '(5%) based on CIF with minimum of 300';
        }
        if (countryCode === 'TR' && taxName === 'EXTRA_TAX') {
            return '(5%) based on CIF for HS Codes starting with 8';
        }
        const config = taxConfigs[countryCode];
        if (!config) return '';
        if (taxName === config.type) {
            return config.formula;
        }
        return '';
    };

    // Function to check if EXCISE should be skipped based on country and HS code
    const shouldSkipExcise = (countryCode, hsCode) => {

        // Skip EXCISE for Malawi (MW) if HS code starts with 85
        if (countryCode === 'MW' && hsCode && hsCode.startsWith('85')) {
            return true;
        }
        return false;
    };

    // Separate specific taxes and others
    const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
    const mainTaxes = {};
    const otherTaxes = [];
    let otherTaxesTotal = 0;

    const countryCode = dhlEstimation.consigneeAddress?.country;

    // Check if we need to remove EXCISE for Malawi and HS code starting with 85
    const itemHsCode = dhlEstimation.customsDetails?.[0]?.hsCode;
    const shouldRemoveExcise = shouldSkipExcise(countryCode, itemHsCode);

    // Process fees and handle special cases
    dhlEstimation.feeTotals.forEach((fee) => {

        // Skip ACD tax for Lebanon
        if (countryCode === 'LB' && fee.name === 'ACD') {
            return;
        }

        // Skip specific fees based on country
        if (fee.name === 'EXCISE' ||
            (shouldRemoveExcise && fee.name === 'EXCISE') ||
            (countryCode === 'EC' && fee.name === 'SPCT') ||
            (countryCode === 'DO' && fee.name === 'CONS') ||
            (countryCode === 'ET' && fee.name === 'SWL') ||
            (countryCode === 'IL' && fee.name === 'TAMA')) {
            return;
        }

        // Only include fees with value > 0
        if (fee.value > 0) {
            if (specificTaxes.includes(fee.name)) {
                if (specialVATCountries.includes(countryCode) &&
                    (fee.name === 'VAT' || fee.name === 'SALE' || fee.name === 'GST')) {
                    const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
                    fee.value = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
                }
                if (fee.value > 0) {
                    mainTaxes[fee.name] = {
                        ...fee,
                        displayName: getTaxDisplayName(fee.name, countryCode)
                    };
                }
            } else {
                if (countryCode === 'AU' && fee.name === 'IMPROC') {
                    fee.value = calculateAUImproc(CIF);
                }
                if (fee.value > 0) {
                    otherTaxes.push(fee);
                    otherTaxesTotal += fee.value;
                }
            }
        }
    });

    // Add Extra Tax for Qatar
    if (countryCode === 'QA' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
        otherTaxes.push({
            name: 'EXTRA_TAX',
            value: 400
        });
        otherTaxesTotal += 400;
    }

    // Add Extra Tax for Turkey
    if (countryCode === 'TR' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
        const extraTaxValue = calculateTRExtraTax(CIF, itemHsCode);
        if (extraTaxValue > 0) {
            otherTaxes.push({
                name: 'EXTRA_TAX',
                value: extraTaxValue
            });
            otherTaxesTotal += extraTaxValue;
        }
    }

    // Add IMPROC for Australia if it doesn't exist in the fees
    if (countryCode === 'AU' && !otherTaxes.some(tax => tax.name === 'IMPROC')) {
        const improcValue = calculateAUImproc(CIF);
        otherTaxes.push({
            name: 'IMPROC',
            value: improcValue
        });
        otherTaxesTotal += improcValue;
    }

    // Add ICF for Comoros if it doesn't exist in the fees
    if (countryCode === 'KM' && !otherTaxes.some(tax => tax.name === 'ICF')) {
        const icfValue = calculateKMIcf(CIF);
        otherTaxes.push({
            name: 'ICF',
            value: icfValue
        });
        otherTaxesTotal += icfValue;
    }

    // Add LAW for Costa Rica if it doesn't exist in the fees
    if (countryCode === 'CR' && !otherTaxes.some(tax => tax.name === 'LAW')) {
        const lawValue = calculateCRLaw(CIF);
        otherTaxes.push({
            name: 'LAW',
            value: lawValue
        });
        otherTaxesTotal += lawValue;
    }

    // Add Extra Tax for Ivory Coast
    if (countryCode === 'CI' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
        const extraTaxValue = calculateCIExtraTax(CIF);
        otherTaxes.push({
            name: 'EXTRA_TAX',
            value: extraTaxValue
        });
        otherTaxesTotal += extraTaxValue;
    }

    // Add Extra Tax for Lebanon
    if (countryCode === 'LB' && !otherTaxes.some(tax => tax.name === 'EXTRA_TAX')) {
        otherTaxes.push({
            name: 'EXTRA_TAX',
            value: 350
        });
        otherTaxesTotal += 350;
    }

    // Special handling for Namibia VAT
    if (countryCode === 'NA' && !mainTaxes['VAT']) {
        const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
        const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
        mainTaxes['VAT'] = {
            name: 'VAT',
            value: vatValue,
            displayName: 'VAT'
        };
    }

    // Special handling for Malawi VAT if not already present
    if (countryCode === 'MW' && !mainTaxes['VAT']) {
        const dutyAmount = dhlEstimation.feeTotals.find(f => f.name === 'DUTY')?.value || 0;
        const vatValue = calculateSpecialTax(dutyAmount, CIF, countryCode, totalItemsValue);
        mainTaxes['VAT'] = {
            name: 'VAT',
            value: vatValue,
            displayName: 'VAT'
        };
    }

    // Calculate total for DHL fees
    const dhlTotal = Object.values(mainTaxes).reduce((sum, fee) => sum + fee.value, 0) + otherTaxesTotal;

    return (
        <table className="w-full border-collapse border border-gray-300">
            <thead>
                <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left">Fee Type</th>
                    <th className="border border-gray-300 p-2 text-right">Amount (USD)</th>
                </tr>
            </thead>
            <tbody>
                {specificTaxes.map((taxName) => {
                    if (mainTaxes[taxName]) {
                        const formulaText = getFormulaTooltip(taxName, countryCode);
                        return (
                            <tr key={taxName}>
                                <td className="border border-gray-300 p-2">
                                    <div className="flex items-center gap-2">
                                        {mainTaxes[taxName].displayName}
                                        {formulaText && (
                                            <div className="group relative">
                                                <Info className="h-4 w-4 text-blue-500 cursor-help" />
                                                <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                                    <p className="text-sm">{formulaText}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="border border-gray-300 p-2 text-right">
                                    {mainTaxes[taxName].value.toFixed(2)}
                                </td>
                            </tr>
                        );
                    }
                    return null;
                })}

                {otherTaxes.length > 0 && (
                    <tr>
                        <td className="border border-gray-300 p-2">
                            <div className="flex items-center gap-2">
                                Taxes
                                <div className="group relative">
                                    <Info className="h-4 w-4 text-gray-500 cursor-help" />
                                    <div className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                        <h4 className="font-semibold mb-2">Tax Breakdown</h4>
                                        {otherTaxes.map((tax) => (
                                            <div key={tax.name} className="flex justify-between text-sm py-1">
                                                <span>{tax.name}:</span>
                                                <span>${tax.value.toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td className="border border-gray-300 p-2 text-right">
                            {otherTaxesTotal.toFixed(2)}
                        </td>
                    </tr>
                )}

                <tr className="font-bold">
                    <td className="border border-gray-300 p-2">Subtotal</td>
                    <td className="border border-gray-300 p-2 text-right">{dhlTotal.toFixed(2)}</td>
                </tr>
            </tbody>
        </table>
    );
};

export default DhlSummaryTable;